/**
 *
 *  Day/Time Selector
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 13/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState } from "react";
import PropTypes from "prop-types"; // use directly
import moment from "moment";

// Components
import TimeSlotsPicker from "../../TimeSlotsPicker/TimeSlotsPicker";
import { withBookingTime } from "../../BookingTime/BookingTime";
import TimeZoneLabel from "../../TimeZoneLabel/TimeZoneLabel";

// Icons
import ArrowLeftIcon from "../../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

// Utils
import { UTCOffsetFromMinutes } from "../../../utils/date";
import * as dates from "../../../utils/dates";

export const BookingFlowDayTimeSelector = React.forwardRef(
  (
    {
      id,
      disabled,
      className,
      isFullyOpen,
      isFetching,
      region,
      partnerAvailabilityLoaded,
      value,
      min,
      max,
      availableDays,
      timeSlots, // from withBookingTime wrapper
      onDateTimeSelect,
      ...rest
    },
    outerRef
  ) => {
    const [currentDate, setCurrentDate] = useState();

    /*
     * @param {Date} dateTime
     * @param {String} dateStr - formatted string
     * @param {boolean} time - is time selected
     *
     */
    const handleDateTimeSelect = (dateTime, dateStr, time) => {
      // Pass through callback to parent
      onDateTimeSelect && onDateTimeSelect(dateTime, time);
    };

    return (
      <div className="day-time-selector tbk-mb-[86px] tbk-flex tbk-flex-col tbk-bg-basic-white">
        {isFullyOpen && partnerAvailabilityLoaded ? (
          <TimeSlotsPicker
            {...rest}
            id={`time-slots-picker${id}`}
            disabled={disabled}
            className={className}
            availableDays={availableDays}
            view="week"
            min={min}
            max={max}
            minTime={"5:30"}
            format="DD MMM, hh:mm A"
            headerFormat="MMMM"
            timeFormat="HH:mm"
            timeSlots={timeSlots}
            step={30}
            value={value}
            currentDate={currentDate}
            region={region}
            timeComponent={({ item }) => (
              <button type="button" disabled={item.disabled}>
                {item.label}
              </button>
            )}
            onSelect={handleDateTimeSelect}
            onCurrentDateChange={(date) => setCurrentDate(date)}
            ref={outerRef}
          />
        ) : (
          <div tabIndex="0" className="tbk-text-main">
            <div
              role="group"
              tabIndex="-1"
              className="tbk-border-b tbk-border-solid tbk-border-[#e3e7eb] tbk-py-3 tbk-px-2"
            >
              <div className="tbk-mb-3 tbk-flex tbk-justify-between">
                <span className="tbk-text-cta tbk-text-grey-main">
                  Select date
                </span>
              </div>
              <div className="">
                <table
                  className="tbk-h-auto tbk-w-full tbk-table-fixed tbk-border-spacing-0 tbk-outline-none"
                  role="grid"
                  tabIndex="-1"
                >
                  <thead className="">
                    <tr className="">
                      <th />
                      <th className="tb-head-cell tbk-text-small-bold tbk-pb-1.5 tbk-text-center tbk-uppercase tbk-text-blue-grey">
                        Sun
                      </th>
                      <th className="tb-head-cell tbk-text-small-bold tbk-pb-1.5 tbk-text-center tbk-uppercase tbk-text-blue-grey">
                        Mon
                      </th>
                      <th className="tb-head-cell tbk-text-small-bold tbk-pb-1.5 tbk-text-center tbk-uppercase tbk-text-blue-grey">
                        Tue
                      </th>
                      <th className="tb-head-cell tbk-text-small-bold tbk-pb-1.5 tbk-text-center tbk-uppercase tbk-text-blue-grey">
                        Wed
                      </th>
                      <th className="tb-head-cell tbk-text-small-bold tbk-pb-1.5 tbk-text-center tbk-uppercase tbk-text-blue-grey">
                        Thu
                      </th>
                      <th className="tb-head-cell tbk-text-small-bold tbk-pb-1.5 tbk-text-center tbk-uppercase tbk-text-blue-grey">
                        Fri
                      </th>
                      <th className="tb-head-cell tbk-text-small-bold tbk-pb-1.5 tbk-text-center tbk-uppercase tbk-text-blue-grey">
                        Sat
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody className="rw-calendar-body">
                    <tr role="row" className="rw-calendar-row">
                      <td className="">
                        <button
                          type="button"
                          className="tbk-h-4 tbk-w-4 tbk-touch-manipulation tbk-rounded-full tbk-bg-basic-white tbk-text-grey-inactive"
                          disabled
                        >
                          <ArrowLeftIcon className="tbk-inline-block" />
                        </button>
                      </td>
                      <td
                        role="gridcell"
                        className="tbk-text-main tbk-h-5 tbk-w-5 tbk-rounded-full tbk-p-0 tbk-text-center"
                      >
                        <div className="tbk-inline-block tbk-h-1 tbk-w-1 tbk-rounded-full tbk-bg-grey-inactive" />
                      </td>
                      <td
                        role="gridcell"
                        className="tbk-text-main tbk-h-5 tbk-w-5 tbk-rounded-full tbk-p-0 tbk-text-center"
                      >
                        <div className="tbk-inline-block tbk-h-1 tbk-w-1 tbk-rounded-full tbk-bg-grey-inactive" />
                      </td>
                      <td
                        role="gridcell"
                        className="tbk-text-main tbk-h-5 tbk-w-5 tbk-rounded-full tbk-p-0 tbk-text-center"
                      >
                        <div className="tbk-inline-block tbk-h-1 tbk-w-1 tbk-rounded-full tbk-bg-grey-inactive" />
                      </td>
                      <td
                        role="gridcell"
                        className="tbk-text-main tbk-h-5 tbk-w-5 tbk-rounded-full tbk-p-0 tbk-text-center"
                      >
                        <div className="tbk-inline-block tbk-h-1 tbk-w-1 tbk-rounded-full tbk-bg-grey-inactive" />
                      </td>
                      <td
                        role="gridcell"
                        className="tbk-text-main tbk-h-5 tbk-w-5 tbk-rounded-full tbk-p-0 tbk-text-center"
                      >
                        <div className="tbk-inline-block tbk-h-1 tbk-w-1 tbk-rounded-full tbk-bg-grey-inactive" />
                      </td>
                      <td
                        role="gridcell"
                        className="tbk-text-main tbk-h-5 tbk-w-5 tbk-rounded-full tbk-p-0 tbk-text-center"
                      >
                        <div className="tbk-inline-block tbk-h-1 tbk-w-1 tbk-rounded-full tbk-bg-grey-inactive" />
                      </td>
                      <td
                        role="gridcell"
                        className="tbk-text-main tbk-h-5 tbk-w-5 tbk-rounded-full tbk-p-0 tbk-text-center"
                      >
                        <div className="tbk-inline-block tbk-h-1 tbk-w-1 tbk-rounded-full tbk-bg-grey-inactive" />
                      </td>
                      <td className="">
                        <button
                          type="button"
                          className="tbk-h-4 tbk-w-4 tbk-touch-manipulation tbk-rounded-full tbk-bg-basic-white tbk-text-grey-inactive"
                        >
                          <ArrowRightIcon className="tbk-inline-block" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="tbk-max-w-94 tbk-py-3 tbk-px-2">
              <div className="tbk-flex tbk-items-center tbk-justify-between">
                <span className="tbk-text-cta tbk-uppercase tbk-text-grey-main">
                  and time
                </span>
              </div>
              <div className="tbk-text-small tbk-mt-0.5 tbk-text-blue-grey">
                Time set to{" "}
                <TimeZoneLabel
                  region={region ? region : process.env.REACT_APP_REGION}
                />
              </div>
              <ul
                tabIndex="-1"
                className="tbk-grid tbk-grid-cols-5 tbk-gap-1 tbk-pt-1.5 tbk-outline-none"
                role="listbox"
              >
                {/*<li role="option" aria-selected="false" className="tbk-text-main tbk-h-6 tbk-w-6 tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-grey-inactive tbk-cursor-disabled tbk-select-none tbk-text-center tbk-outline-none">
                    05:30
                  </li>*/}
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  5:30
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  6:00
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  7:00
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  7:30
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  8:00
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  8:30
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  9:00
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  9:30
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  10:00
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  10:30
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  11:00
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  11:30
                  <div className="tbk-text-small !tbk-leading-none">AM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  12:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  12:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  1:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  1:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  2:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  2:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  3:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  3:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  4:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  4:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  5:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  5:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  6:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  6:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  7:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  7:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  8:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  8:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  9:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  9:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  10:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  10:30
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
                <li
                  role="option"
                  aria-selected="false"
                  className="tbk-text-main tbk-h-6 tbk-w-6 tbk-cursor-disabled tbk-select-none tbk-rounded-full tbk-py-2 tbk-px-0 tbk-text-center !tbk-text-lg !tbk-leading-4 tbk-text-grey-inactive tbk-outline-none"
                >
                  11:00
                  <div className="tbk-text-small !tbk-leading-none">PM</div>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  }
);

BookingFlowDayTimeSelector.propTypes = {
  id: PropTypes.string.isRequired,
  isFullyOpen: PropTypes.bool,
  isFetching: PropTypes.bool,
  partnerAvailabilityLoaded: PropTypes.bool,
};

export default withBookingTime()(BookingFlowDayTimeSelector);
